type GoogleMapsCoordinate = number | (() => number);

interface GoogleMapsLatLng {
  lat?: GoogleMapsCoordinate;
  lng?: GoogleMapsCoordinate;
}

export interface GoogleMapsAddressComponent {
  long_name?: string;
  short_name?: string;
  longText?: string;
  shortText?: string;
  types: string[];
}

export interface GoogleMapsPlace {
  formatted_address?: string;
  formattedAddress?: string;
  displayName?: string;
  name?: string;
  address_components?: GoogleMapsAddressComponent[];
  addressComponents?: GoogleMapsAddressComponent[];
  location?: GoogleMapsLatLng | null;
  geometry?: {
    location?: GoogleMapsLatLng | null;
  };
  fetchFields?: (request: { fields: string[] }) => Promise<void>;
}

interface GoogleMapsLegacyStructuredFormatting {
  main_text?: string;
  secondary_text?: string;
}

interface GoogleMapsLegacyAutocompletePrediction {
  description?: string;
  place_id?: string;
  structured_formatting?: GoogleMapsLegacyStructuredFormatting;
}

type GoogleMapsPlacesStatus = string;

interface GoogleMapsLegacyAutocompleteService {
  getPlacePredictions: (
    request: GoogleMapsLegacyAutocompleteRequest,
    callback: (
      predictions: GoogleMapsLegacyAutocompletePrediction[] | null,
      status: GoogleMapsPlacesStatus,
    ) => void,
  ) => void;
}

interface GoogleMapsLegacyPlaceDetailsRequest {
  placeId: string;
  fields: string[];
}

interface GoogleMapsLegacyPlacesService {
  getDetails: (
    request: GoogleMapsLegacyPlaceDetailsRequest,
    callback: (place: GoogleMapsPlace | null, status: GoogleMapsPlacesStatus) => void,
  ) => void;
}

export interface GoogleMapsPlacePrediction {
  text?: {
    toString: () => string;
  };
  mainText?: {
    toString: () => string;
  };
  secondaryText?: {
    toString: () => string;
  };
  toPlace: () => GoogleMapsPlace;
}

export interface GoogleMapsAutocompleteSuggestion {
  placePrediction?: GoogleMapsPlacePrediction;
}

export type GoogleMapsAutocompleteSessionToken = object;

interface GoogleMapsGeocoderRequest {
  address?: string;
  componentRestrictions?: {
    country?: string;
  };
  location?: {
    lat: number;
    lng: number;
  };
  region?: string;
}

interface GoogleMapsGeocoderResult {
  formatted_address?: string;
  geometry?: {
    location?: GoogleMapsLatLng | null;
  };
}

interface GoogleMapsGeocoderResponse {
  results: GoogleMapsGeocoderResult[];
}

interface GoogleMapsGeocoder {
  geocode: (request: GoogleMapsGeocoderRequest) => Promise<GoogleMapsGeocoderResponse>;
}

export interface GoogleMapsGeocodingLibrary {
  Geocoder?: new () => GoogleMapsGeocoder;
}

export interface GoogleMapsMapInstance {
  setCenter?: (position: { lat: number; lng: number }) => void;
}

export interface GoogleMapsMarkerInstance {
  setMap: (map: GoogleMapsMapInstance | null) => void;
}

export interface GoogleMapsCircleInstance {
  setMap: (map: GoogleMapsMapInstance | null) => void;
}

export interface GoogleMapsMapLibrary {
  Map?: new (
    element: HTMLElement,
    options: {
      center: { lat: number; lng: number };
      zoom: number;
      disableDefaultUI?: boolean;
      zoomControl?: boolean;
      gestureHandling?: 'auto' | 'cooperative' | 'greedy' | 'none';
      mapTypeControl?: boolean;
      streetViewControl?: boolean;
      fullscreenControl?: boolean;
    },
  ) => GoogleMapsMapInstance;
  Circle?: GoogleMapsCircleConstructor;
}

export interface GoogleMapsMarkerConstructor {
  new (options: {
    map: GoogleMapsMapInstance;
    position: { lat: number; lng: number };
    title?: string;
  }): GoogleMapsMarkerInstance;
}

export interface GoogleMapsCircleConstructor {
  new (options: {
    map: GoogleMapsMapInstance;
    center: { lat: number; lng: number };
    radius: number;
    fillColor?: string;
    fillOpacity?: number;
    strokeColor?: string;
    strokeOpacity?: number;
    strokeWeight?: number;
  }): GoogleMapsCircleInstance;
}

export interface GoogleMapsGeocodingResult {
  formattedAddress: string;
  latitude: number;
  longitude: number;
}

export interface GoogleMapsAutocompleteRequest {
  input: string;
  includedPrimaryTypes?: string[];
  includedRegionCodes?: string[];
  language?: string;
  region?: string;
  sessionToken?: GoogleMapsAutocompleteSessionToken;
}

interface GoogleMapsLegacyAutocompleteRequest {
  input: string;
  componentRestrictions?: {
    country?: string | string[];
  };
  language?: string;
  region?: string;
  sessionToken?: GoogleMapsAutocompleteSessionToken;
  types?: string[];
}

export interface GoogleMapsAutocompleteSuggestionClass {
  fetchAutocompleteSuggestions: (
    request: GoogleMapsAutocompleteRequest,
  ) => Promise<{ suggestions: GoogleMapsAutocompleteSuggestion[] }>;
}

export interface GoogleMapsPlacesLibrary {
  AutocompleteSessionToken?: new () => GoogleMapsAutocompleteSessionToken;
  AutocompleteSuggestion?: GoogleMapsAutocompleteSuggestionClass;
  AutocompleteService?: new () => GoogleMapsLegacyAutocompleteService;
  PlacesService?: new (element: HTMLElement) => GoogleMapsLegacyPlacesService;
  PlacesServiceStatus?: {
    OK?: GoogleMapsPlacesStatus;
    ZERO_RESULTS?: GoogleMapsPlacesStatus;
  };
}

interface GoogleMapsApi {
  importLibrary?: (
    libraryName: 'places' | 'geocoding' | 'maps',
  ) => Promise<GoogleMapsPlacesLibrary | GoogleMapsGeocodingLibrary | GoogleMapsMapLibrary>;
  places?: GoogleMapsPlacesLibrary;
  Geocoder?: new () => GoogleMapsGeocoder;
  Map?: GoogleMapsMapLibrary['Map'];
  Marker?: GoogleMapsMarkerConstructor;
  Circle?: GoogleMapsCircleConstructor;
}

interface GoogleMapsWindow extends Window {
  google?: {
    maps?: GoogleMapsApi;
  };
  gm_authFailure?: () => void;
}

interface SecurityPolicyViolationLike extends Event {
  blockedURI?: string;
  effectiveDirective?: string;
  violatedDirective?: string;
}

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-api';
const GOOGLE_MAPS_SCRIPT_STATUS_ATTR = 'data-worklynx-status';
const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 6000;
const GOOGLE_MAPS_OPERATION_TIMEOUT_MS = 6000;

let loadPromise: Promise<GoogleMapsApi | null> | null = null;
let placesLoadPromise: Promise<GoogleMapsPlacesLibrary | null> | null = null;
let geocodingLoadPromise: Promise<GoogleMapsGeocodingLibrary | null> | null = null;
let mapLoadPromise: Promise<GoogleMapsMapLibrary | null> | null = null;
let addressSearchWarmupPromise: Promise<void> | null = null;
let shouldSkipNewPlacesAutocomplete = false;

function getGoogleMapsApi(): GoogleMapsApi | null {
  return (window as GoogleMapsWindow).google?.maps ?? null;
}

function loadGoogleMapsApi(): Promise<GoogleMapsApi | null> {
  const existingApi = getGoogleMapsApi();
  if (existingApi) {
    return Promise.resolve(existingApi);
  }

  if (loadPromise) {
    return loadPromise;
  }

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim();
  if (!apiKey) {
    return Promise.resolve(null);
  }

  loadPromise = new Promise((resolve, reject) => {
    const mapsWindow = window as GoogleMapsWindow;
    const existingScript = document.getElementById(
      GOOGLE_MAPS_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    let scriptElement = existingScript;

    if (scriptElement?.getAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR) === 'failed') {
      scriptElement.remove();
      scriptElement = null;
    }

    const previousAuthFailure = mapsWindow.gm_authFailure;
    let isSettled = false;
    let handleSecurityPolicyViolation: ((event: Event) => void) | null = null;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      mapsWindow.gm_authFailure = previousAuthFailure;
      scriptElement?.removeEventListener('load', handleLoad);
      scriptElement?.removeEventListener('error', handleError);
      if (handleSecurityPolicyViolation) {
        document.removeEventListener('securitypolicyviolation', handleSecurityPolicyViolation);
      }
    };

    const settle = (callback: () => void) => {
      if (isSettled) return;
      isSettled = true;
      cleanup();
      callback();
    };

    const handleLoad = () => {
      settle(() => {
        const api = getGoogleMapsApi();
        if (api) {
          scriptElement?.setAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR, 'ready');
          resolve(api);
          return;
        }

        loadPromise = null;
        scriptElement?.setAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR, 'failed');
        scriptElement?.remove();
        reject(new Error('Google Maps JavaScript API loaded without an available maps object.'));
      });
    };
    const handleError = () => {
      settle(() => {
        loadPromise = null;
        scriptElement?.setAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR, 'failed');
        scriptElement?.remove();
        reject(new Error('Failed to load Google Maps JavaScript API.'));
      });
    };

    handleSecurityPolicyViolation = (event: Event) => {
      const violation = event as SecurityPolicyViolationLike;
      const blockedUri = violation.blockedURI ?? '';
      const blockedGoogleMaps =
        blockedUri.startsWith('https://maps.googleapis.com') ||
        blockedUri.startsWith('https://maps.gstatic.com');

      if (!blockedGoogleMaps) return;

      settle(() => {
        loadPromise = null;
        scriptElement?.setAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR, 'failed');
        scriptElement?.remove();
        reject(new Error('Google Maps JavaScript API was blocked by Content Security Policy.'));
      });
    };

    document.addEventListener('securitypolicyviolation', handleSecurityPolicyViolation);

    const timeoutId = window.setTimeout(() => {
      settle(() => {
        loadPromise = null;
        scriptElement?.setAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR, 'failed');
        scriptElement?.remove();
        reject(new Error('Google Maps JavaScript API did not finish loading.'));
      });
    }, GOOGLE_MAPS_LOAD_TIMEOUT_MS);

    mapsWindow.gm_authFailure = () => {
      previousAuthFailure?.();
      settle(() => {
        loadPromise = null;
        scriptElement?.setAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR, 'failed');
        scriptElement?.remove();
        reject(new Error('Google Maps API key is not authorized for this API target.'));
      });
    };

    if (scriptElement) {
      scriptElement.addEventListener('load', handleLoad, { once: true });
      scriptElement.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    scriptElement = script;
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.setAttribute(GOOGLE_MAPS_SCRIPT_STATUS_ATTR, 'loading');
    script.async = true;
    script.defer = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      '&v=weekly&libraries=places&loading=async';
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

  return loadPromise;
}

export async function loadGoogleMapsPlaces(): Promise<GoogleMapsPlacesLibrary | null> {
  const api = await loadGoogleMapsApi();
  if (!api) {
    return null;
  }

  if (api.importLibrary) {
    if (!placesLoadPromise) {
      placesLoadPromise = withTimeout(
        api.importLibrary('places'),
        GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
        'Google Maps Places library did not finish loading.',
      )
        .then((library) => ({
          ...(api.places ?? {}),
          ...(library as GoogleMapsPlacesLibrary),
        }))
        .catch((error) => {
          placesLoadPromise = null;
          throw error;
        });
    }

    return placesLoadPromise;
  }

  return api.places ?? null;
}

export async function loadGoogleMapsMap(): Promise<{
  Map: NonNullable<GoogleMapsMapLibrary['Map']>;
  Marker: GoogleMapsMarkerConstructor | null;
  Circle: GoogleMapsCircleConstructor | null;
} | null> {
  const api = await loadGoogleMapsApi();
  if (!api) return null;

  if (!mapLoadPromise) {
    mapLoadPromise = api.importLibrary
      ? withTimeout(
          api.importLibrary('maps'),
          GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
          'Google Maps map library did not finish loading.',
        )
          .then((library) => library as GoogleMapsMapLibrary)
          .catch((error) => {
            mapLoadPromise = null;
            throw error;
          })
      : Promise.resolve({ Map: api.Map });
  }

  const library = await mapLoadPromise;
  const MapConstructor = library?.Map ?? api.Map;
  if (!MapConstructor) return null;

  return {
    Map: MapConstructor,
    Marker: api.Marker ?? null,
    Circle: library?.Circle ?? api.Circle ?? null,
  };
}

export async function warmGoogleMapsAddressSearch(): Promise<void> {
  if (typeof window === 'undefined') return;

  if (!addressSearchWarmupPromise) {
    addressSearchWarmupPromise = Promise.allSettled([
      loadGoogleMapsPlaces(),
      loadGoogleMapsGeocoding(),
    ]).then((results) => {
      if (results.some((result) => result.status === 'rejected')) {
        addressSearchWarmupPromise = null;
      }
    });
  }

  await addressSearchWarmupPromise;
}

export async function fetchGoogleMapsAutocompleteSuggestions(
  places: GoogleMapsPlacesLibrary,
  request: GoogleMapsAutocompleteRequest,
): Promise<GoogleMapsAutocompleteSuggestion[]> {
  let newAutocompleteError: unknown = null;

  if (places.AutocompleteSuggestion && !shouldSkipNewPlacesAutocomplete) {
    try {
      const { suggestions } = await withTimeout(
        places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request),
        GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
        'Google Places autocomplete request timed out.',
      );
      return suggestions;
    } catch (error) {
      newAutocompleteError = error;

      if (!isGoogleMapsAuthorizationError(error) || !places.AutocompleteService) {
        throw error;
      }

      shouldSkipNewPlacesAutocomplete = true;
    }
  }

  if (places.AutocompleteService) {
    return fetchLegacyGoogleMapsAutocompleteSuggestions(places, request);
  }

  if (newAutocompleteError) {
    throw newAutocompleteError;
  }

  return [];
}

async function fetchLegacyGoogleMapsAutocompleteSuggestions(
  places: GoogleMapsPlacesLibrary,
  request: GoogleMapsAutocompleteRequest,
): Promise<GoogleMapsAutocompleteSuggestion[]> {
  if (!places.AutocompleteService) {
    return [];
  }

  const service = new places.AutocompleteService();
  const predictions = await withTimeout(
    new Promise<GoogleMapsLegacyAutocompletePrediction[]>((resolve, reject) => {
      service.getPlacePredictions(
        {
          input: request.input,
          componentRestrictions: {
            country: (request.includedRegionCodes ?? ['au']).map((code) => code.toLowerCase()),
          },
          language: request.language,
          region: request.region,
          sessionToken: request.sessionToken,
        },
        (results, status) => {
          const okStatus = places.PlacesServiceStatus?.OK ?? 'OK';
          const zeroResultsStatus = places.PlacesServiceStatus?.ZERO_RESULTS ?? 'ZERO_RESULTS';

          if (status === okStatus) {
            resolve(results ?? []);
            return;
          }

          if (status === zeroResultsStatus) {
            resolve([]);
            return;
          }

          reject(new Error(`Google Places legacy autocomplete failed with status ${status}.`));
        },
      );
    }),
    GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
    'Google Places legacy autocomplete request timed out.',
  );

  return predictions.map((prediction) => toLegacyAutocompleteSuggestion(places, prediction));
}

function toLegacyAutocompleteSuggestion(
  places: GoogleMapsPlacesLibrary,
  prediction: GoogleMapsLegacyAutocompletePrediction,
): GoogleMapsAutocompleteSuggestion {
  const text = prediction.description?.trim() ?? '';
  const mainText = prediction.structured_formatting?.main_text?.trim();
  const secondaryText = prediction.structured_formatting?.secondary_text?.trim();

  return {
    placePrediction: {
      text: { toString: () => text },
      mainText: mainText ? { toString: () => mainText } : undefined,
      secondaryText: secondaryText ? { toString: () => secondaryText } : undefined,
      toPlace: () => createLegacyGoogleMapsPlace(places, prediction, text),
    },
  };
}

function createLegacyGoogleMapsPlace(
  places: GoogleMapsPlacesLibrary,
  prediction: GoogleMapsLegacyAutocompletePrediction,
  fallbackAddress: string,
): GoogleMapsPlace {
  const place: GoogleMapsPlace = {
    formatted_address: fallbackAddress,
    formattedAddress: fallbackAddress,
    displayName: fallbackAddress,
    name: fallbackAddress,
  };

  place.fetchFields = async () => {
    const details = await fetchLegacyGoogleMapsPlaceDetails(places, prediction.place_id).catch(
      () => null,
    );

    if (details) {
      Object.assign(place, details);
      return;
    }

    const geocoded = await geocodeGoogleMapsAddress(fallbackAddress);
    if (geocoded) {
      Object.assign(place, {
        formatted_address: geocoded.formattedAddress,
        formattedAddress: geocoded.formattedAddress,
        location: {
          lat: geocoded.latitude,
          lng: geocoded.longitude,
        },
        geometry: {
          location: {
            lat: geocoded.latitude,
            lng: geocoded.longitude,
          },
        },
      });
    }
  };

  return place;
}

async function fetchLegacyGoogleMapsPlaceDetails(
  places: GoogleMapsPlacesLibrary,
  placeId: string | undefined,
): Promise<GoogleMapsPlace | null> {
  if (!placeId || !places.PlacesService) {
    return null;
  }

  const container = document.createElement('div');
  const service = new places.PlacesService(container);

  return withTimeout(
    new Promise<GoogleMapsPlace | null>((resolve, reject) => {
      service.getDetails(
        {
          placeId,
          fields: ['formatted_address', 'geometry', 'name', 'address_components'],
        },
        (place, status) => {
          const okStatus = places.PlacesServiceStatus?.OK ?? 'OK';
          const zeroResultsStatus = places.PlacesServiceStatus?.ZERO_RESULTS ?? 'ZERO_RESULTS';

          if (status === okStatus) {
            resolve(place);
            return;
          }

          if (status === zeroResultsStatus) {
            resolve(null);
            return;
          }

          reject(new Error(`Google Places legacy details failed with status ${status}.`));
        },
      );
    }),
    GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
    'Google Places legacy details request timed out.',
  );
}

export async function loadGoogleMapsGeocoding(): Promise<GoogleMapsGeocodingLibrary | null> {
  const api = await loadGoogleMapsApi();
  if (!api) {
    return null;
  }

  if (api.importLibrary) {
    if (!geocodingLoadPromise) {
      geocodingLoadPromise = withTimeout(
        api.importLibrary('geocoding'),
        GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
        'Google Maps geocoding library did not finish loading.',
      )
        .then((library) => library as GoogleMapsGeocodingLibrary)
        .catch((error) => {
          geocodingLoadPromise = null;
          throw error;
        });
    }

    return geocodingLoadPromise;
  }

  return api.Geocoder ? { Geocoder: api.Geocoder } : null;
}

export async function geocodeGoogleMapsAddress(
  address: string,
): Promise<GoogleMapsGeocodingResult | null> {
  const query = address.trim();
  if (!query) return null;

  const geocoding = await loadGoogleMapsGeocoding();
  if (!geocoding?.Geocoder) {
    return null;
  }

  const geocoder = new geocoding.Geocoder();
  const { results } = await withTimeout(
    geocoder.geocode({
      address: query,
      componentRestrictions: { country: 'AU' },
      region: 'au',
    }),
    GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
    'Google Maps geocoding request timed out.',
  );

  const result = results.find((item) => item.formatted_address && item.geometry?.location);
  const coordinates = getGoogleMapsPlaceCoordinates(result);

  if (!result?.formatted_address || !coordinates) {
    return null;
  }

  return {
    formattedAddress: result.formatted_address,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  };
}

export async function reverseGeocodeGoogleMapsCoordinates({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): Promise<string | null> {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  const geocoding = await loadGoogleMapsGeocoding();
  if (!geocoding?.Geocoder) {
    return null;
  }

  const geocoder = new geocoding.Geocoder();
  const { results } = await withTimeout(
    geocoder.geocode({
      location: { lat: latitude, lng: longitude },
      region: 'au',
    }),
    GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
    'Google Maps reverse geocoding request timed out.',
  );

  return results.find((item) => item.formatted_address)?.formatted_address?.trim() ?? null;
}

export async function fetchGoogleMapsPlaceFields(
  place: GoogleMapsPlace,
  fields: string[],
): Promise<void> {
  if (!place.fetchFields) {
    return;
  }

  await withTimeout(
    place.fetchFields({ fields }),
    GOOGLE_MAPS_OPERATION_TIMEOUT_MS,
    'Google Places place details request timed out.',
  );
}

export function getGoogleMapsPlaceAddressComponents(
  place: GoogleMapsPlace | null | undefined,
): GoogleMapsAddressComponent[] {
  return place?.addressComponents ?? place?.address_components ?? [];
}

export function getGoogleMapsPlaceCoordinates(
  place: GoogleMapsPlace | null | undefined,
): { latitude: number; longitude: number } | null {
  const location = place?.location ?? place?.geometry?.location;
  const latitude = readGoogleMapsCoordinate(location?.lat);
  const longitude = readGoogleMapsCoordinate(location?.lng);

  if (latitude == null || longitude == null) {
    return null;
  }

  return { latitude, longitude };
}

function readGoogleMapsCoordinate(coordinate: GoogleMapsCoordinate | undefined): number | null {
  const value = typeof coordinate === 'function' ? coordinate() : coordinate;

  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function isGoogleMapsAuthorizationError(error: unknown): boolean {
  const text = getErrorSearchText(error);

  return /403|forbidden|permission_denied|request_denied|api[_\s-]?target[_\s-]?blocked|referer|referrer|unauthori[sz]ed|api[_\s-]?not[_\s-]?activated/i.test(
    text,
  );
}

function getErrorSearchText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (error && typeof error === 'object') {
    return Object.entries(error)
      .map(([key, value]) => `${key} ${String(value)}`)
      .join(' ');
  }

  return String(error);
}

export function getGoogleMapsSuggestionText(suggestion: GoogleMapsAutocompleteSuggestion): string {
  const prediction = suggestion.placePrediction;
  const fullText = prediction?.text?.toString();

  if (fullText) {
    return fullText;
  }

  return [prediction?.mainText?.toString(), prediction?.secondaryText?.toString()]
    .filter(Boolean)
    .join(', ');
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(message));
    }, timeoutMs);

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}
